import wait from "marketingWebsite/utils/wait.js"

import { MOBILE_BREAKPOINT } from "marketingWebsite/utils/constants.js"

const INTRO_BLOCK_TYPE = "intro_block"
const TOOLTIP_CLASS_NAME = "pricingTooltip"
const TOOLTIP_LEFT_CLASS_NAME = `${ TOOLTIP_CLASS_NAME }--left`

export default function useFeatureTooltips () {
    let controller
    let isTooltipActive = false
    const dialog = document.getElementById( "featureTooltip" )
    const titleEl = dialog.querySelector( `.${ TOOLTIP_CLASS_NAME }-title` )
    const contentEl = dialog.querySelector( `.${ TOOLTIP_CLASS_NAME }-content` )
    const imageEl = dialog.querySelector( `.${ TOOLTIP_CLASS_NAME }-image` )
    const closeDialogButton = dialog.querySelector( `.${ TOOLTIP_CLASS_NAME }-closeButton` )
    const links = document.querySelectorAll( ".pricing-linkWithTooltip" )

    links.forEach( ( link ) => {
        link.addEventListener(
            "click",
            async ( event ) => await handleLinkClick( link, event )
        )
        link.addEventListener(
            "mouseenter",
            async () => !isMobile() && await showTooltip( link )
        )
        link.addEventListener(
            "mouseout",
            () => !isMobile() && removeTooltip()
        )
    })

    dialog.addEventListener( "click", handleDialogClick )
    closeDialogButton.addEventListener( "click", removeTooltip )

    let isResizing = false
    window.addEventListener( "resize", onResize )
    window.addEventListener( "mouseup", onStopResize )

    function isMobile () {
        return window.innerWidth <= MOBILE_BREAKPOINT
    }

    // removes HTML tags and returns pure text
    function removeTags ( text = "" ) {
        const tagRegex = /<[^>]+(>|$)/g

        return text.replace( tagRegex, "" )
    }

    function setDialogPosition ( element ) {
        const { height, left, right, top } = element.getBoundingClientRect()
        const halfElementHeight = height / 2

        // determines whether the tooltip should appear from the left or right side of the link
        const isOnLeft = right > window.innerWidth / 2

        const dialogLeftPosition = isOnLeft ? left : right
        const dialogTopPosition = Math.round( top + halfElementHeight )

        dialog.classList.toggle( TOOLTIP_LEFT_CLASS_NAME, isOnLeft )

        Object.assign( dialog.style, {
            left: `${ dialogLeftPosition }px`,
            top: `${ dialogTopPosition }px`,
        })

        return {
            left: dialogLeftPosition,
            top: dialogTopPosition,
        }
    }

    // adjust tooltip position if it doesn't fit in the window
    function setDialogPositionOffset ( element, dialogPosition ) {
        const dialogRect = dialog.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        const halfElementHeight = elementRect.height / 2

        if ( dialogRect.top < 0 ) {
            const topPosition = dialogPosition.top - dialogRect.top + halfElementHeight
            dialog.style.top = `${ Math.round( topPosition ) }px`
        }
        else if ( dialogRect.bottom > viewportHeight ) {
            const offScreenHeight = dialogRect.bottom - viewportHeight
            const topPosition = dialogPosition.top - offScreenHeight - halfElementHeight

            dialog.style.top = `${ Math.round( topPosition ) }px`
        }
    }

    function updateTooltipContent ({ type, fields }) {
        const feature = {}

        if ( type === INTRO_BLOCK_TYPE ) {
            feature.title = fields.introduction_h2
            feature.content = fields.value_prop_headline
        }
        else {
            feature.title = fields.support_headline
            feature.content = fields.support_text
        }

        titleEl.innerHTML = feature.title
        // received content may contain HTML tags that spoil the appearance of the tooltip,
        // hence it need to remove them from the text
        contentEl.innerHTML = removeTags( feature.content )

        if ( fields.media ) {
            imageEl.style.display = ""
            imageEl.src = fields.media
            imageEl.alt = fields.media_alt ? fields.media_alt : ""
        }
        else {
            imageEl.style.display = "none"
        }

        return dialog
    }

    function removeTooltip () {
        isTooltipActive = false
        dialog.close()
        controller && controller.abort()

        dialog.classList.remove( TOOLTIP_LEFT_CLASS_NAME )

        Object.assign( dialog.style, {
            left: "",
            top: "",
        })

        return
    }

    function handleDialogClick ( event ) {
        // remove tooltip if user clicks outisde tooltip
        if ( event.target === dialog ) {
            removeTooltip()
        }
    }

    async function handleLinkClick ( item, event ) {
        event.preventDefault()

        isMobile() && await showTooltip( item )

        return
    }

    async function showTooltip ( item ) {
        isTooltipActive = true
        dialog.close()

        const data = await fetchFeature( item )

        if ( !data || !Object.keys( data ).length ) {
            return
        }

        updateTooltipContent( data )

        // if the user moves the mouse beyond the link
        // then cancel showing the tooltip
        if ( !isTooltipActive ) {
            return
        }

        if ( isMobile() ) {
            // show tooltip as a modal with backdrop on mobile
            dialog.showModal()
        }
        else {
            const dialogPosition = setDialogPosition( item )
            dialog.show()

            // give time for loading images
            await wait( 150 )
            // to get proper offset calculation
            setDialogPositionOffset( item, dialogPosition )
        }

        return
    }

    async function fetchFeature ( item ) {
        try {
            controller = new AbortController()
            const { signal } = controller
            const { tooltipSlug } = item.dataset
            const url = `/feature/${ tooltipSlug.toLowerCase() }/`

            // delay fetch to allow abort request when user moves quickly over elements
            // it helps to reduce the number of unnecessary requests
            await wait( 100 )
            const response = await fetch( url, { signal })
            const feature = await response.json()

            return feature
        }
        catch ( e ) {
            // do nothing
        }

        return null
    }

    function onResize ( e ) {
        if ( isResizing ) {
            return
        }

        removeTooltip()
        isResizing = true
    }

    function onStopResize ( e ) {
        isResizing = false
    }
}
